/* src/ui/toast.ts — the one user-facing failure channel (CLAUDE.md Errors).
 *
 * Never `alert()` — it blocks the render loop and cannot be styled. Never a silent `catch` —
 * a failure the player cannot see is a bug report we never receive.
 *
 * Toasts are transient and non-blocking on purpose: the world keeps rendering underneath. A
 * failure in one domain (a price that would not set, a label layer that 503s) must not take
 * the map down with it (PLAN.md Part 2 §8).
 */

import { el } from './dom.ts';

const VISIBLE_MS = 4_500;
/** Beyond this, older toasts are dropped rather than stacking off the top of the screen. */
const MAX_VISIBLE = 3;

let host: HTMLElement | null = null;

function ensureHost(): HTMLElement {
  if (host?.isConnected === true) return host;
  host = el('div', { class: 'gf-toasts', attrs: { role: 'status', 'aria-live': 'polite' } });
  document.body.appendChild(host);
  return host;
}

export function toast(message: string): void {
  const parent = ensureHost();
  while (parent.childElementCount >= MAX_VISIBLE) parent.firstElementChild?.remove();

  const node = el('div', { class: 'gf-toast' }, message);
  parent.appendChild(node);

  window.setTimeout(() => {
    node.classList.add('is-leaving');
    // Matches the CSS fade so the node is removed after it has finished animating out.
    window.setTimeout(() => {
      node.remove();
    }, 220);
  }, VISIBLE_MS);
}

/** Test seam — drops the host so a test can assert against a clean DOM. */
export function resetToasts(): void {
  host?.remove();
  host = null;
}
